<template>
  <dialog ref="dialog" :class="[$style.config, $style.wrap]">
    <button
      :class="$style.closeBtn"
      :aria-label="localizedStrings['close-modal'] || 'Close Modal'"
      autofocus
      @click="handleCloseDialog"
    >
      <IconClose :class="$style.closeIcon" alt="" />
    </button>

    <slot />
  </dialog>
</template>

<script setup lang="ts">
const { localizedStrings } = useLocalizedStrings()

const dialogEl = useTemplateRef<HTMLDialogElement>('dialog')

const emit = defineEmits(['close-dialog'])

const props = defineProps<{
  visible: boolean
}>()

const isVisible = ref(props.visible || false)

// Watch visibility from parent
watch(
  () => props.visible,
  (visible) => {
    isVisible.value = visible
  },
)

watch(isVisible, (isVisible) => {
  if (isVisible) {
    dialogEl.value?.showModal?.()
  } else {
    dialogEl.value?.close?.()
  }
})

const handleCloseDialog = () => {
  emit('close-dialog')
  isVisible.value = false
}

onMounted(() => {
  dialogEl.value?.addEventListener?.('cancel', () => {
    handleCloseDialog()
  })
})
</script>

<style module lang="scss">
.config {
  --close-button-offset: 20px;
  --close-button-dimensions: 40px;
}

.wrap {
  // Remove default style
  background-color: transparent;
  border: none;
  margin: auto;

  padding: calc(var(--close-button-offset) + var(--close-button-dimensions)) 0 0;

  &[open] {
    opacity: 1;
  }
}

/*   Closed state of the dialog   */
dialog {
  opacity: 0;
  transition:
    opacity 0.7s ease-out,
    overlay 0.7s ease-out allow-discrete,
    display 0.7s ease-out allow-discrete;
  /* Equivalent to
  transition: all 0.7s allow-discrete; */
}

/*   Before-open state  */
/* Needs to be after the previous dialog[open] rule to take effect,
    as the specificity is the same */
@starting-style {
  dialog[open] {
    opacity: 0;
    transform: scaleY(0);
  }
}

/* Transition the :backdrop when the dialog modal is promoted to the top layer */
dialog::backdrop {
  background-color: rgb(255 255 255 / 0%);
  transition:
    display 0.7s allow-discrete,
    overlay 0.7s allow-discrete,
    background-color 0.7s;
  /* Equivalent to
  transition: all 0.7s allow-discrete; */
}

dialog[open]::backdrop {
  background-color: rgb(255 255 255 / 80%);
}

/* This starting-style rule cannot be nested inside the above selector
because the nesting selector cannot represent pseudo-elements. */

@starting-style {
  dialog[open]::backdrop {
    background-color: rgb(255 255 255 / 0%);
  }
}

.closeBtn {
  position: absolute;
  top: var(--close-button-offset);
  right: var(--close-button-offset);
  // z-index: 3;

  background-color: var(--purple);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: grid;
  height: var(--close-button-dimensions);
  place-content: center;
  width: var(--close-button-dimensions);
}

.closeIcon {
  color: var(--white);
  width: 25px;
  height: 25px;
}
</style>
